import { useAppDispatch, useAppSelector } from 'app/hooks';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import ImageUploadModal from 'components/common/ImageUploadModal';
import useEscapeKey from 'components/common/UseEscapeKey/UseEscapeKey';
import React, { ChangeEvent, forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Button, Col, Form, Image, Modal, ModalProps, Row, Table } from 'react-bootstrap';
import 'react-datepicker/dist/react-datepicker.css';
import ReactDOMServer from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import { NoImageIcon } from 'theme/images';
import { getCurrency, svgToDataURL } from 'utils/common';
import { IMAGE_SIZES } from 'utils/constants';
import { Menu, MenuDetail } from '../../MenuList/models/menu';
import { MAIN_MENU } from '../constants';
import menuDetailService from '../menuDetailService';
import { menuDetailActions, selectMenu } from '../menuDetailSlice';
import { MenuDetailView } from '../models/menuDetail';
import { MenuView } from '../models/viewMenu';

const SubMenuDetailModal = forwardRef<
  ModalProps,
  { onMenuModalClose?: () => void; menuCallBack?: (obj: {} | undefined) => void }
>((props, ref) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // selectMenu
  const menuSelector = useAppSelector(selectMenu);
  //ref
  let menuImgFileRef = useRef<HTMLImageElement>(null);
  let menuImgName = useRef('');

  //state
  const [showModal, setShowModal] = useState(false);
  const [selectedMenuDetailView, setSelectedMenuDetailView] = useState<MenuView>();
  const [selectedMenu, setSelectedMenu] = useState<Menu>();
  const [menuDetails, setMenuDetails] = useState<MenuDetailView[]>();
  const [showImgUploadModal, setShowImgUploadModal] = useState(false);
  //external menu
  const [selectedMenuId, setSelectedMenuId] = useState<number>();

  useEffect(() => {
    if (selectedMenuId && menuSelector && Object.keys(menuSelector).length > 0) {
      let menu_view = new MenuView(menuSelector);
      setSelectedMenuDetailView(menu_view);
      setSelectedMenu(menu_view.menu);
      menuImgName.current = menu_view.menu.img || '';
      setShowModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuSelector, selectedMenuId]); // call from external page

  //useImperativeHandle
  useImperativeHandle(ref, () => ({
    closeModal() {
      setShowModal(false);
      setSelectedMenuDetailView(undefined);
      setSelectedMenu(undefined);
    },
    openModal(menu_detail: MenuDetail, expired_date: Date) {
      if (menu_detail) {
        if (selectedMenu === undefined || menu_detail.menu.id !== selectedMenu.id) {
          let menu_view = new MenuView(
            menu_detail.menu,
            menu_detail.start_date.toString(),
            menu_detail.end_date.toString()
          );
          getMenuDetails(menu_detail.menu.id, expired_date);
          setSelectedMenuDetailView(menu_view);
          setSelectedMenu(menu_view.menu);
          menuImgName.current = menu_view.menu.img || '';
        }
        setShowModal(true);
      }
    },
    // call from external parent page
    openModalExternal(menu_id: number, data_date: Date) {
      if (menu_id) {
        dispatch(menuDetailActions.fetchMenu({ menu_id: menu_id, expired_date: data_date })); //YYYY-MM-DD
        setSelectedMenuId(menu_id);
        getMenuDetails(menu_id, data_date);
      }
    },
    onReset() {
      setSelectedMenuId(undefined);
      setSelectedMenuDetailView(undefined);
      setSelectedMenu(undefined);
      dispatch(menuDetailActions.removeStateMenu({} as Menu));
    },
  }));

  const getMenuDetails = async (menu_id: number, str_date: Date) => {
    try {
      const response = await menuDetailService.getMenuDetails({ menu_id: menu_id, expired_date: str_date });
      setMenuDetails(response.data);
    } catch (error) {
      console.error('Error fetching CSV:', error);
    }
  };

  const handleChangeMenuAttribute = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const property_name = event.currentTarget.name;
    const value = event.currentTarget.value;
    if (selectedMenu) {
      const obj = {
        [property_name]: value,
      };
      setSelectedMenu({ ...selectedMenu, img: menuImgName.current, ...obj });
    }
  };

  const handleSaveMenuUpdate = () => {
    if (selectedMenu) {
      let menuClone = { ...selectedMenu };
      menuClone.img = menuClone.img || '';
      //menuClone.menu_poscodes = [];
      menuClone.menu_properties = [];
      dispatch(menuDetailActions.patchSubMenu(menuClone));
      handleCloseModal(true);
    }
  };

  const handleCloseModal = (is_update = false) => {
    if (is_update && props.menuCallBack !== undefined) {
      const menuCloneObject = { ...selectedMenu, img: menuImgName.current };
      props.menuCallBack(menuCloneObject);
      //dispatch(menuDetailActions.removeStateMessage(undefined));
    }
    if (props.onMenuModalClose !== undefined) {
      props.onMenuModalClose();
    }
    setShowModal(false);
  };

  const handleResFromUploadImgModal = (filename: string, image_path: string) => {
    if (menuImgFileRef && menuImgFileRef.current) {
      if (selectedMenu) {
        menuImgName.current = filename;
        setSelectedMenu({ ...selectedMenu, img: filename });
      }
    }
  };

  const getSvgDataUrl = useCallback(() => {
    const SvgComponent = React.memo(() => (
      <NoImageIcon
        width={150}
        height={100}
        display_txt={`${IMAGE_SIZES.menu_img.minWidth}x${IMAGE_SIZES.menu_img.minHeight}~${IMAGE_SIZES.menu_img.maxWidth}x${IMAGE_SIZES.menu_img.maxHeight}`}
      />
    ));
    const svgString = ReactDOMServer.renderToString(<SvgComponent />);
    return svgToDataURL(svgString);
  }, []);

  // Function to convert JSX to data URL
  useEscapeKey(handleCloseModal);

  return (
    <>
      {showImgUploadModal && (
        <ImageUploadModal
          imagepath={process.env.REACT_APP_IMAGE_URL + menuImgName.current}
          minWidth={IMAGE_SIZES.menu_img.minWidth}
          minHeight={IMAGE_SIZES.menu_img.minHeight}
          maxWidth={IMAGE_SIZES.menu_img.maxWidth}
          maxHeight={IMAGE_SIZES.menu_img.maxHeight}
          maxFileSize={IMAGE_SIZES.menu_img.maxFileSize}
          onReflect={handleResFromUploadImgModal}
          onClose={() => {
            setShowImgUploadModal(false);
          }}
        ></ImageUploadModal>
      )}
      <div className="static-modal">
        <Modal animation={true} size="xl" centered={true} show={showModal} backdrop={'static'} keyboard={false}>
          <Modal.Body>
            <div className="row">
              <div className="col-xs-10 col-sm-3">
                <FeatureTitleBar title={t('menuDetailFeature.Text.menu_detail_sub')}></FeatureTitleBar>
              </div>
              <div className="col text-end">
                <button type="button" className="btn-close" onClick={() => handleCloseModal()}></button>
              </div>
            </div>
            <Row className="mb-3">
              <Row>
                <Col className="col-sm-9 col-xs-12">
                  <div className="mb-3 d-flex justify-content-between">
                    <div>
                      <h5>{t('menuDetailFeature.Text.menu_settings')}</h5>
                      <div dangerouslySetInnerHTML={{ __html: t('menuDetailFeature.Text.notice_text') }}></div>
                    </div>
                    <div className="d-flex align-items-end">
                      <button onClick={handleSaveMenuUpdate} type="button" className="btn btn-sm btn--dark-green">
                        {t('menuDetailFeature.Component.save_btn')}
                      </button>
                      <div className="ps-5">{t('menuDetailFeature.Text.register_information')}</div>
                    </div>
                    <div></div>
                  </div>
                </Col>
              </Row>
              <Col className="col-sm-6 col-xs-12">
                <div>
                  <Table hover className="table table-bordered text-center align-middle mb-3">
                    <tbody>
                      <tr>
                        <td className="table-primary" colSpan={2}>
                          {t('menuDetailFeature.Text.menu_name')}
                        </td>
                        <td>{selectedMenu?.food_system_menu?.name || ''}</td>
                      </tr>
                      <tr>
                        <td className="table-primary" colSpan={2}>
                          {t('menuDetailFeature.Text.menu_image')}
                        </td>
                        <td>
                          <div className="text-center d-flex justify-content-around mt-3 mb-3">
                            <div>
                              {
                                <Image
                                  style={{
                                    width: 150,
                                    height: 100,
                                  }}
                                  ref={menuImgFileRef}
                                  src={
                                    menuImgName.current
                                      ? process.env.REACT_APP_IMAGE_URL + menuImgName.current
                                      : getSvgDataUrl()
                                  }
                                  alt="No Image"
                                />
                              }
                            </div>
                            <div className="align-self-end">
                              <Button
                                onClick={() => {
                                  setShowImgUploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary" rowSpan={6}>
                          {t('menuDetailFeature.Text.display_name')}
                        </td>
                        <td className="table-primary">{t('menuDetailFeature.Text.japanese')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Control
                                name="display_name_ja"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.display_name_ja ? selectedMenu.display_name_ja : ''}
                              ></Form.Control>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.english')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Control
                                name="display_name_en"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.display_name_en ? selectedMenu.display_name_en : ''}
                              ></Form.Control>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.simplified_chinese')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Control
                                name="display_name_zh_hans"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={
                                  selectedMenu?.display_name_zh_hans ? selectedMenu.display_name_zh_hans : ''
                                }
                              ></Form.Control>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.traditional_chinese')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Control
                                name="display_name_zh_hant"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={
                                  selectedMenu?.display_name_zh_hant ? selectedMenu.display_name_zh_hant : ''
                                }
                              ></Form.Control>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.korean')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Control
                                name="display_name_ko"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.display_name_ko ? selectedMenu.display_name_ko : ''}
                              ></Form.Control>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.vietnamese')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Control
                                name="display_name_vi"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.display_name_vi ? selectedMenu.display_name_vi : ''}
                              ></Form.Control>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary" rowSpan={6}>
                          {t('menuDetailFeature.Text.explanation')}
                        </td>
                        <td className="table-primary">{t('menuDetailFeature.Text.japanese')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Control
                                name="explanation_ja"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.explanation_ja ? selectedMenu.explanation_ja : ''}
                              ></Form.Control>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.english')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Control
                                name="explanation_en"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.explanation_en ? selectedMenu.explanation_en : ''}
                              ></Form.Control>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.simplified_chinese')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Control
                                name="explanation_zh_hans"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.explanation_zh_hans ? selectedMenu.explanation_zh_hans : ''}
                              ></Form.Control>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.traditional_chinese')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Control
                                name="explanation_zh_hant"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.explanation_zh_hant ? selectedMenu.explanation_zh_hant : ''}
                              ></Form.Control>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.korean')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Control
                                name="explanation_ko"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.explanation_ko ? selectedMenu.explanation_ko : ''}
                              ></Form.Control>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.vietnamese')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Control
                                name="explanation_vi"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.explanation_vi ? selectedMenu.explanation_vi : ''}
                              ></Form.Control>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary" colSpan={2}>
                          {t('menuDetailFeature.Text.remarks')}
                        </td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <textarea
                                className="form-control"
                                rows={4}
                                name="note"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.note ? selectedMenu.note : ''}
                              ></textarea>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
              </Col>
              <Col className="col-sm-6 col-xs-12">
                <div>
                  <Table hover className="table table-bordered text-center align-middle mb-3">
                    <tbody>
                      <tr>
                        <td className="table-primary" rowSpan={2}>
                          {t('menuDetailFeature.Table.date_of_expired')}
                        </td>
                        <td className="table-primary">{t('menuDetailFeature.Table.start')}</td>
                        <td>
                          {selectedMenuDetailView?.startDate ||
                            (menuDetails && menuDetails.length > 0
                              ? menuDetails[0].start_date.toString().replaceAll('-', '/')
                              : '') ||
                            ''}
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Table.end')}</td>
                        <td>
                          {selectedMenuDetailView?.endDate ||
                            (menuDetails && menuDetails.length > 0
                              ? menuDetails[0].end_date.toString().replaceAll('-', '/')
                              : '') ||
                            ''}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
                <div>
                  <Table hover className="table table-bordered text-center align-middle mb-3">
                    <thead>
                      <tr className="align-middle">
                        <th className="table-primary">{t('menuDetailFeature.Table.pos_cd')}</th>
                        <th className="table-primary">{t('menuDetailFeature.Table.pos_name')}</th>
                        <th className="table-primary">{t('menuDetailFeature.Table.amount_of_money_excluding_tax')}</th>
                        <th
                          className="table-primary"
                          dangerouslySetInnerHTML={{
                            __html: t('menuDetailFeature.Table.amount_of_money_including_tax'),
                          }}
                        ></th>
                      </tr>
                    </thead>
                    <tbody>
                      {menuDetails &&
                        menuDetails.map((menu_detail, index) => {
                          return (
                            <tr key={index}>
                              <td>{menu_detail.pos_code.cd}</td>
                              <td>{menu_detail.name}</td>
                              <td>
                                {getCurrency(menu_detail.standard_price) ?? t('menuDetailFeature.Text.not_obtained')}
                              </td>
                              <td>
                                {menu_detail.is_registered
                                  ? (menu_detail.menu_type * 1 === MAIN_MENU
                                      ? getCurrency(
                                          menu_detail.pos_code.main_price,
                                          menu_detail.pos_code.main_price_flg === 2
                                        )
                                      : getCurrency(
                                          menu_detail.pos_code.composition_price,
                                          menu_detail.pos_code.composition_price_flg === 2
                                        )) ?? t('menuDetailFeature.Text.not_obtained')
                                  : t('menuDetailFeature.Text.not_obtained')}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </Table>
                </div>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <button className="btn btn--red" onClick={() => handleCloseModal()}>
              {t('menuDetailFeature.Component.close')}
            </button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
});

export default SubMenuDetailModal;
